import {
  APPRAISAL_RED_FLAG_LTV_CAP,
  BANK_BALANCE_THRESHOLDS,
  CREDIT_SCORE_THRESHOLDS,
  EXPERIENCE_TIER_TABLE,
  INELIGIBLE_EXPERIENCE_TIER,
  LOAN_CAPS,
  LOC_LTV,
  LTV_SIZE_LIMIT_TIERS,
  MAX_LTV_CAP,
  NET_WORTH_THRESHOLDS,
  POPULATION_LOW_LTC_HAIRCUT,
  PRESALE_DEPOSIT_THRESHOLD,
} from '../constants';
import type {
  AdjustmentItem,
  EstimatorInput,
  EstimatorResult,
  ExperienceTierData,
  LineOfCreditScenario,
} from '../types';

// ── Experience tier derivation ────────────────────────────────────────────────

/**
 * S33: built score = new construction + MIN(3, fix-flip) + MIN(10, rentals) + GC bonus.
 * Using a qualified GC adds 3 to the score (S32).
 */
export function computeBuiltScore(input: EstimatorInput): number {
  return (
    input.newConstructionCount
    + Math.min(3, input.fixFlipCount)
    + Math.min(10, input.rentalCount)
    + (input.hasQualifiedGC ? 3 : 0)
  );
}

/**
 * V32: good repayments = MAX(0, ROUNDDOWN(total BFI repayments − 2 × payment issues, 0)).
 * Each payment issue deducts two repayments from the score (M36 = I36 × 2).
 */
export function computeGoodRepayments(input: EstimatorInput): number {
  return Math.max(0, Math.floor(input.bfiRepaymentCount - input.bfiPaymentIssueCount * 2));
}

/**
 * AB41: minimum qualifying experience level (1 = most experienced, 10 = least).
 *
 * Replicates MIN(AA30:AA39) where each cell returns the level number when
 * both criteria are met:
 *   Z28 (builtScore + goodRepayments) ≥ C-column threshold  AND
 *   AF28 (goodRepayments alone)        ≥ D-column threshold
 *
 * Returns 0 (ineligible) if the borrower doesn't meet the level-10 minimum.
 * The Excel returns #N/A from the VLOOKUP in this case, which propagates to
 * 0% LTV and 90% LTC cap (equivalent to ltvHaircut=0, feeRate=0).
 */
export function computeExperienceLevel(builtScore: number, goodRepayments: number): number {
  const combined = builtScore + goodRepayments;
  const qualifying = EXPERIENCE_TIER_TABLE.filter(
    (t) => combined >= t.minCombinedScore && goodRepayments >= t.minRepayments,
  );
  if (qualifying.length === 0) return 0;
  return Math.min(...qualifying.map((t) => t.level));
}

/** Look up the full tier row for a given level (0–10). Level 0 = ineligible. */
export function lookupExperienceTier(level: number): ExperienceTierData {
  if (level === 0) return INELIGIBLE_EXPERIENCE_TIER;
  const tier = EXPERIENCE_TIER_TABLE.find((t) => t.level === level);
  if (!tier) throw new RangeError(`Experience level must be 0–10, got ${level}`);
  return tier;
}

// ── Land cost basis ───────────────────────────────────────────────────────────

function landAgeYears(landPurchaseDate: Date | undefined): number {
  if (!landPurchaseDate) return 0;
  const purchase = new Date(landPurchaseDate);
  if (isNaN(purchase.getTime())) return 0;
  return (Date.now() - purchase.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
}

/**
 * M19: total imputed cost used in C4.
 *   If land age ≤ 1 year → use acquisition cost (cost basis).
 *   If land age > 1 year  → use MIN(landValue, acquisitionCost × 1.5) (value basis).
 * Then add construction cost.
 */
function totalImputedCost(input: EstimatorInput): number {
  const age = landAgeYears(input.landPurchaseDate);
  const landBasis =
    age > 1
      ? Math.min(input.landValue, input.landAcquisitionCost * 1.5)
      : input.landAcquisitionCost;
  return landBasis + input.constructionCost;
}

/** M20: actual total cost = land acquisition cost + construction cost. C4 is capped at this. */
function actualTotalCost(input: EstimatorInput): number {
  return input.landAcquisitionCost + input.constructionCost;
}

// ── LTV size limit (C8 / I8) ─────────────────────────────────────────────────

/**
 * I8 = IF(valuePerUnit > $2M, 65%, IF(valuePerUnit > $1M, 70%, 75%))
 * C8 = I8 × appraisedValue
 */
export function computeC8(valueAsComplete: number, numberOfUnits: number): number {
  if (numberOfUnits <= 0) return LTV_SIZE_LIMIT_TIERS.baseRate * valueAsComplete;
  const perUnit = valueAsComplete / numberOfUnits;
  const rate =
    perUnit > LTV_SIZE_LIMIT_TIERS.highValuePerUnit
      ? LTV_SIZE_LIMIT_TIERS.highRate
      : perUnit > LTV_SIZE_LIMIT_TIERS.midValuePerUnit
        ? LTV_SIZE_LIMIT_TIERS.midRate
        : LTV_SIZE_LIMIT_TIERS.baseRate;
  return rate * valueAsComplete;
}

// ── Other constraints (C9) ────────────────────────────────────────────────────

/**
 * C9: "Other" — additional LTV caps from appraisal issues and very low bank balance.
 *
 *   M48: appraisal red flags → 70% LTV cap (L45 formula)
 *   M58: bank balance < $25K → 55% or 60% LTV cap
 *   N48: MIN(applicable LTV caps) × valueAsComplete
 *
 * Returns Infinity when no constraint applies ("n/a").
 */
function computeC9(input: EstimatorInput): number {
  const ltvCaps: number[] = [];

  if (!input.appraisalFreeOfRedFlags) ltvCaps.push(APPRAISAL_RED_FLAG_LTV_CAP);

  // M58 uses ABS() for the outer guard so a negative overdraft balance still
  // triggers the cap, but the inner thresholds compare the raw value (negative
  // is always < 25K and < 10K, so a negative balance maps to the 55% cap).
  if (Math.abs(input.bankBalance) > 0 && input.bankBalance < BANK_BALANCE_THRESHOLDS.c9Cap) {
    ltvCaps.push(
      input.bankBalance < BANK_BALANCE_THRESHOLDS.c9LowCap ? 0.55 : 0.60,
    );
  }

  if (ltvCaps.length === 0) return Infinity;
  return Math.min(...ltvCaps) * input.valueAsComplete;
}

// ── Scoring matrix (rows 45–67) ───────────────────────────────────────────────

/**
 * Full AF/AG/AH/AI adjustment matrix.
 *
 * AG70 = SUM(AF68:AI68) = net LTV adjustment (H3).
 * I3 = ltvHaircut + MIN(0, AG70) — positive bonuses are ignored for LTV.
 *
 * AK49 (pre-sale) is also captured here for convenience but used separately in ltcHaircut.
 */
export function calculateScoreAdjustments(input: EstimatorInput): AdjustmentItem[] {
  const hasMediumPreSale =
    input.preSoldUnderContract &&
    input.preSaleDepositPct !== null &&
    input.preSaleDepositPct < PRESALE_DEPOSIT_THRESHOLD;
  const hasLargePreSale =
    input.preSoldUnderContract &&
    input.preSaleDepositPct !== null &&
    input.preSaleDepositPct >= PRESALE_DEPOSIT_THRESHOLD;

  return [
    // ── MARKET (rows 45–49) ──────────────────────────────────────────────────
    // AF45: −3% if population = "Moderate-Low"
    { label: 'Population density (Moderate-Low)', value: input.populationDensity === 'Moderate-Low' ? -0.03 : 0 },
    // AF46: −3% if growth = "Negative"
    { label: 'Population growth (Negative)', value: input.populationGrowth === 'Negative' ? -0.03 : 0 },
    // AF47: −3% if affordability = "Poor"
    { label: 'Affordability (Poor)', value: input.affordability === 'Poor' ? -0.03 : 0 },
    // AG47: −1.5% if affordability = "Weak"
    { label: 'Affordability (Weak)', value: input.affordability === 'Weak' ? -0.015 : 0 },
    // AI47: +3% if affordability = "Excellent" (positive — ignored by I3)
    { label: 'Affordability (Excellent)', value: input.affordability === 'Excellent' ? 0.03 : 0 },
    // AI45: +3% if Dense AND Fast growth (positive — ignored by I3)
    { label: 'Dense market with fast growth', value: (input.populationDensity === 'Dense' && input.populationGrowth === 'Fast') ? 0.03 : 0 },
    // AH49: +1.5% LTV if pre-sale with small deposit (positive — ignored by I3)
    { label: 'Pre-sale (deposit < 2.5%)', value: hasMediumPreSale ? 0.015 : 0 },
    // AI49: +3% LTV if pre-sale with large deposit (positive — ignored by I3)
    { label: 'Pre-sale (deposit ≥ 2.5%)', value: hasLargePreSale ? 0.03 : 0 },

    // ── FINANCIALS (rows 52–54) ───────────────────────────────────────────────
    // AF52: −3% if NW < $500K
    { label: 'Net worth (< $500K)', value: input.netWorth < NET_WORTH_THRESHOLDS.ltvPenalty ? -0.03 : 0 },
    // AG52/AH52: +1.5% if NW > $3.5M (strictly greater — AI52's +3% requires combined
    // guarantor-table total ≥ $5M which is not modelled here)
    { label: 'Net worth (> $3.5M)', value: input.netWorth > NET_WORTH_THRESHOLDS.bonusFloor ? 0.015 : 0 },
    // AF53: −3% if bank < $50K (strictly less than)
    { label: 'Bank balance (< $50K)', value: input.bankBalance < BANK_BALANCE_THRESHOLDS.heavyPenalty ? -0.03 : 0 },
    // AG53: −1.5% if $50K < bank < $75K (strictly between)
    { label: 'Bank balance ($50K–$75K)', value: (input.bankBalance > BANK_BALANCE_THRESHOLDS.heavyPenalty && input.bankBalance < BANK_BALANCE_THRESHOLDS.lightPenalty) ? -0.015 : 0 },
    // AH53: +1.5% if $250K ≤ bank < $500K (positive — ignored by I3)
    { label: 'Bank balance ($250K–$500K)', value: (input.bankBalance >= BANK_BALANCE_THRESHOLDS.bonusSmall && input.bankBalance < BANK_BALANCE_THRESHOLDS.bonusLarge) ? 0.015 : 0 },
    // AI53: +3% if bank ≥ $500K (positive — ignored by I3)
    { label: 'Bank balance (≥ $500K)', value: input.bankBalance >= BANK_BALANCE_THRESHOLDS.bonusLarge ? 0.03 : 0 },
    // AF54: −3% if credit ≤ 620
    { label: 'Credit score (≤ 620)', value: input.creditScore <= CREDIT_SCORE_THRESHOLDS.minimum ? -0.03 : 0 },
    // AG54: −1.5% if 620 < credit ≤ 699
    { label: 'Credit score (621–699)', value: (input.creditScore > CREDIT_SCORE_THRESHOLDS.minimum && input.creditScore <= CREDIT_SCORE_THRESHOLDS.fair) ? -0.015 : 0 },
    // AH54: +1.5% if 740 ≤ credit < 780 (positive — ignored by I3)
    { label: 'Credit score (740–779)', value: (input.creditScore >= CREDIT_SCORE_THRESHOLDS.good && input.creditScore < CREDIT_SCORE_THRESHOLDS.excellent) ? 0.015 : 0 },
    // AI54: +3% if credit ≥ 780 (positive — ignored by I3)
    { label: 'Credit score (≥ 780)', value: input.creditScore >= CREDIT_SCORE_THRESHOLDS.excellent ? 0.03 : 0 },

    // ── BACKGROUND FLAGS (rows 60–66) — each −3% LTV ─────────────────────────
    { label: 'Criminal/felony conviction', value: input.hasCriminalConviction ? -0.03 : 0 },
    { label: 'Litigation with financial institution', value: input.hasLitigation ? -0.03 : 0 },
    { label: 'Outstanding real estate liens', value: input.hasOutstandingLiens ? -0.03 : 0 },
    { label: 'Delinquent on real estate loan', value: input.hasRealEstateDelinquency ? -0.03 : 0 },
    { label: 'Delinquent to supplier', value: input.hasSupplierDelinquency ? -0.03 : 0 },
    { label: 'Foreclosure within past 10 years', value: input.hasForeclosure ? -0.03 : 0 },
    { label: 'Bankruptcy within past 10 years', value: input.hasBankruptcy ? -0.03 : 0 },

    // ── BFI PAYMENT ISSUE (row 67) — separate: −3% LTV + 5% LTC haircut ─────
    { label: 'BFI loan payment issue', value: input.hasBfiPaymentIssue ? -0.03 : 0 },
  ];
}

// ── LTC haircut (AK68) ────────────────────────────────────────────────────────

/**
 * AK68: sum of all AK-column values.
 * Positive = haircut (reduces LTC); negative = bonus (pre-sale).
 *
 * I4 = MIN(90%, (1 − feeRate) − totalLtcHaircut)
 */
function computeLtcHaircut(input: EstimatorInput): number {
  let h = 0;

  // AK45: Low population density → 10% haircut
  if (input.populationDensity === 'Low') h += POPULATION_LOW_LTC_HAIRCUT;

  // AK49: pre-sale deposit → negative haircut (bonus to LTC)
  if (input.preSoldUnderContract && input.preSaleDepositPct !== null) {
    h -= input.preSaleDepositPct < PRESALE_DEPOSIT_THRESHOLD ? 0.025 : 0.05;
  }

  // AK52: NW < $100K → 5% haircut
  if (input.netWorth < NET_WORTH_THRESHOLDS.ltcHaircut) h += 0.05;

  // AK53: bank < $5K → 5% haircut
  if (input.bankBalance < BANK_BALANCE_THRESHOLDS.ltcHaircut) h += 0.05;

  // AK54: credit < 600 → 5% haircut; credit < 550 → additional 5%
  if (input.creditScore < CREDIT_SCORE_THRESHOLDS.low) h += 0.05;
  if (input.creditScore < CREDIT_SCORE_THRESHOLDS.veryLow) h += 0.05;

  // AK67: BFI payment issue → 5% haircut (separate from background flags)
  if (input.hasBfiPaymentIssue) h += 0.05;

  return h;
}

// ── Line of credit (N25 = M7) ─────────────────────────────────────────────────
//
// LOC has an entirely separate constraint set from the construction loan (rows
// 113–136 in docs/Estimator-ORIG.xlsx):
//   J4 = base LTV from a tiered table (74/72/70/68% by units & per-unit value)
//   K4 = sum of LOC-specific adjustments (rows 122–134)
//   L4 = J4 + MIN(0, K4)   — positive adjustments are ignored
//   M4 = L4 × appraised_value
//   M5 = 100% × total cost
//   M6 = $5,000,000
//   M7 = MIN(M4, M5, M6)
//
// Inputs not present in our schema (I46, I47 = marketing-time and conforming
// flags) are empty in the original sheet and evaluate to 0 there, so they are
// omitted here.

/** J4 = I118: LOC base LTV tier (Excel rows 115–118). */
export function computeLocBaseLtv(appraisedValue: number, numberOfUnits: number): number {
  if (numberOfUnits <= 0) return LOC_LTV.base;
  if (numberOfUnits >= LOC_LTV.fivePlusUnitThreshold) return LOC_LTV.fivePlusUnitRate;
  if (numberOfUnits === 1) {
    return appraisedValue > LOC_LTV.singleUnitHighValueCap
      ? LOC_LTV.singleUnitHighValueRate
      : LOC_LTV.base;
  }
  const perUnit = appraisedValue / numberOfUnits;
  return perUnit > LOC_LTV.multiUnitPerUnitCap ? LOC_LTV.multiUnitHighRate : LOC_LTV.base;
}

/**
 * K4 = I136: sum of LOC adjustments (rows 122–134).
 *
 *   I122 liquidity:   bank > 0 AND bank < appraised → −3%
 *   I123 credit:      credit < 620 → −3%
 *   I125 litigation:  hasLitigation → −3%
 *   I126 RE delinq:   hasRealEstateDelinquency → −3%
 *   I128 mech. lien:  hasOutstandingLiens → −3%
 *   I129 presale:     deposit ≥ 2.5% → +3%
 *   I130 pop growth:  Negative → −3%, Flat → −1.5%
 *   I131 pop density: Low → −3%
 *   I132 appraisal:   !appraisalFreeOfRedFlags → −3%
 *   I133/I134 omitted (reference empty cells I46/I47)
 */
export function computeLocAdjustments(input: EstimatorInput): number {
  let a = 0;
  if (input.bankBalance > 0 && input.bankBalance < input.valueAsComplete) a -= 0.03;
  if (input.creditScore > 0 && input.creditScore < CREDIT_SCORE_THRESHOLDS.minimum) a -= 0.03;
  if (input.hasLitigation) a -= 0.03;
  if (input.hasRealEstateDelinquency) a -= 0.03;
  if (input.hasOutstandingLiens) a -= 0.03;
  if (
    input.preSoldUnderContract
    && input.preSaleDepositPct !== null
    && input.preSaleDepositPct >= PRESALE_DEPOSIT_THRESHOLD
  ) a += 0.03;
  if (input.populationGrowth === 'Negative') a -= 0.03;
  else if (input.populationGrowth === 'Flat') a -= 0.015;
  if (input.populationDensity === 'Low') a -= 0.03;
  if (!input.appraisalFreeOfRedFlags) a -= 0.03;
  return a;
}

export function computeLineOfCredit(input: EstimatorInput): LineOfCreditScenario {
  const baseLtv = computeLocBaseLtv(input.valueAsComplete, input.numberOfUnits);
  const netLtvAdjustment = computeLocAdjustments(input);
  const effectiveLtv = baseLtv + Math.min(0, netLtvAdjustment);

  const ltvCap = effectiveLtv * input.valueAsComplete;
  const costCap = actualTotalCost(input);
  const sizeCap = LOAN_CAPS.lineOfCredit;

  const rawMaxLoanAmount = Math.min(ltvCap, costCap, sizeCap);
  const maxLoanAmount = Math.max(0, rawMaxLoanAmount);
  const qualificationStatus: 'approved' | 'conditional' =
    netLtvAdjustment < -0.05 ? 'conditional' : 'approved';
  const equityRequired = Math.max(0, costCap - rawMaxLoanAmount);
  const isSmallLoan = rawMaxLoanAmount < LOAN_CAPS.smallLoanThreshold;

  return {
    maxLoanAmount,
    qualificationStatus,
    equityRequired,
    isSmallLoan,
    baseLtv,
    netLtvAdjustment,
    effectiveLtv,
    ltvCap,
    costCap,
    sizeCap,
  };
}

// ── Main entry point ──────────────────────────────────────────────────────────

/**
 * Replicates the full Matrix sheet calculation.
 *
 * Key cells:
 *   G3 = ltvHaircut (from tier table, AK36)
 *   H3 = AG70 = net LTV adjustment (SUM of all AF/AG/AH/AI adjustments)
 *   I3 = G3 + MIN(0, H3)          — effectiveLtv, positive bonuses ignored
 *
 *   G4 = 1 − feeRate              (AK37 column)
 *   AK68 = totalLtcHaircut        (SUM of all AK values)
 *   I4 = MIN(90%, G4 − AK68)      — effectiveLtc
 *
 *   C3  = I3 × valueAsComplete
 *   C4  = MIN(I4 × totalImputedCost, actualTotalCost)
 *   C5  = "n/a" (land owned) | constructionCost × subLandAdvanceFactor
 *   C6  = $5,000,000 (standard) | $5,000,000 (LOC)
 *   C8  = ltvSizeLimitRate × valueAsComplete
 *   C9  = MIN(appraisal/liquidity LTV cap × AV)  or Infinity if no cap
 *   C10 = MIN(C3, C4, C5, C6, C8, C9)
 */
export function calculateMaxLoan(input: EstimatorInput): EstimatorResult {
  // ── Experience ──────────────────────────────────────────────────────────────
  const builtScore = computeBuiltScore(input);
  const goodRepayments = computeGoodRepayments(input);
  const combinedScore = builtScore + goodRepayments;
  const experienceLevel = computeExperienceLevel(builtScore, goodRepayments);
  const tier = lookupExperienceTier(experienceLevel);

  // ── LTV (I3) ────────────────────────────────────────────────────────────────
  const adjustments = calculateScoreAdjustments(input);
  const netLtvAdjustment = adjustments.reduce((sum, a) => sum + a.value, 0);
  const effectiveLtv = tier.ltvHaircut + Math.min(0, netLtvAdjustment);

  // ── LTC (I4) ────────────────────────────────────────────────────────────────
  const totalLtcHaircut = computeLtcHaircut(input);
  const effectiveLtc = Math.min(MAX_LTV_CAP, (1 - tier.feeRate) - totalLtcHaircut);

  // ── Loan scenarios (C3–C9) ──────────────────────────────────────────────────
  const imputed = totalImputedCost(input);
  const actual = actualTotalCost(input);

  const c3 = effectiveLtv * input.valueAsComplete;
  const c4 = Math.min(effectiveLtc * imputed, actual);
  const c5 = input.landOwned
    ? Infinity
    : input.constructionCost * tier.subLandAdvanceFactor;
  const c6 = LOAN_CAPS.default;
  const c8 = computeC8(input.valueAsComplete, input.numberOfUnits);
  const c9 = computeC9(input);

  // E10 = ROUNDUP(C10, -2) — round up to nearest $100.
  // Round to nearest cent first to strip IEEE 754 sub-cent noise (e.g. 1−0.18
  // = 0.8200000000000001, causing × 2,200,000 → 1,804,000.0000000002 which
  // would incorrectly overshoot to $1,804,100).
  const rawMin = Math.min(c3, c4, c5, c6, c8, c9);
  const rawMaxLoanAmount = Math.ceil(Math.round(rawMin * 100) / 100 / 100) * 100;
  const maxLoanAmount = Math.max(0, rawMaxLoanAmount);

  // ── Derived outputs ─────────────────────────────────────────────────────────
  const qualificationStatus = netLtvAdjustment < -0.05 ? 'conditional' : 'approved';
  const closingCostEstimate = rawMaxLoanAmount * 0.02;
  const equityRequired = Math.max(0, actual - rawMaxLoanAmount);
  const isSmallLoan = rawMaxLoanAmount < LOAN_CAPS.smallLoanThreshold;

  return {
    maxLoanAmount,
    effectiveLtv,
    effectiveLtc,
    qualificationStatus,
    experienceTier: tier,
    experienceLevel,
    builtScore,
    goodRepayments,
    combinedScore,
    adjustments,
    netLtvAdjustment,
    totalLtcHaircut,
    c3_ltv: c3,
    c4_ltc: c4,
    c5_subLand: c5,
    c6_cap: c6,
    c8_sizeLimit: c8,
    c9_other: c9,
    closingCostEstimate,
    equityRequired,
    isSmallLoan,
    lineOfCredit: computeLineOfCredit(input),
  };
}
